import React from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { Check } from "lucide-react";

export default function ServiceModels() {
  const { t, lang } = useLanguage();
  const { settings } = useSiteSettings();

  // Use admin-saved plans if present, else fall back to translations
  const plans = Array.isArray(settings.models) && settings.models.length > 0
    ? settings.models.map((p) => ({
        name: p[`name_${lang}`] || p.name_de,
        price: p[`price_${lang}`] || p.price_de,
        unit: p[`unit_${lang}`] || p.unit_de || "",
        desc: p[`desc_${lang}`] || p.desc_de,
        features: p[`features_${lang}`] || p.features_de || [],
        featured: !!p.featured,
      }))
    : t.models.plans;

  return (
    <section id="models" data-testid="models-section" className="py-20 md:py-24 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 mb-12 gap-y-8">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono-tech text-[#1d428a]">
              <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
              {t.models.eyebrow}
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 data-testid="models-title" className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A0A0A]">
              {t.models.title}
            </h2>
            <p className="mt-5 text-[#0A0A0A] text-sm md:text-base max-w-2xl leading-relaxed">
              {t.models.lead}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1d428a]/15">
          {plans.map((p, i) => (
            <div
              key={p.name}
              data-testid={`model-card-${i}`}
              className={`relative p-7 md:p-8 ${
                p.featured ? "bg-[#1d428a] text-white" : "bg-white text-[#0A0A0A]"
              }`}
            >
              {p.featured && (
                <span className="absolute top-4 right-4 font-mono-tech text-[#df4661]">· EMPFOHLEN</span>
              )}
              <div className={`font-mono-tech mb-2 ${p.featured ? "text-white/70" : "text-[#df4661]"}`}>
                MODELL {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-display text-3xl md:text-4xl">{p.name}</div>

              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-display text-4xl md:text-5xl">{p.price}</span>
                {p.unit && (
                  <span className={`font-mono-tech ${p.featured ? "text-white/70" : "text-[#5A6270]"}`}>
                    {p.unit}
                  </span>
                )}
              </div>

              <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-white/80" : "text-[#5A6270]"}`}>
                {p.desc}
              </p>

              <div className={`mt-6 h-px ${p.featured ? "bg-white/20" : "bg-[#1d428a]/15"}`} />

              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check
                      size={15}
                      strokeWidth={2}
                      className={p.featured ? "text-[#df4661] mt-0.5" : "text-[#1d428a] mt-0.5"}
                    />
                    <span className="text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid={`model-cta-${i}`}
                className={`mt-8 inline-flex items-center gap-2 font-mono-tech px-5 py-3 transition-colors ${
                  p.featured
                    ? "bg-[#df4661] text-white hover:bg-white hover:text-[#1d428a]"
                    : "border border-[#1d428a] text-[#1d428a] hover:bg-[#1d428a] hover:text-white"
                }`}
              >
                {t.nav.cta} →
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
